import {CampaignTargetType} from '../__generated__/graphql'

export const isValidHttpUrlWithDomain = (url?: string): boolean => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:'
    const hasDotDomain = parsed.hostname.includes('.')
    return isHttp && hasDotDomain
  } catch {
    return false
  }
}

export const validateCampaign = ({ landingPage }: { landingPage: string }) => {
  if (!isValidHttpUrlWithDomain(landingPage)) {
    // eslint-disable-next-line no-throw-literal
    throw [['landingPage', { type: 'manual', message: 'Landing Page must be a valid URL with http/https and a domain' }]]
  }
}

const WHITESPACE = /\s/

// True if the URL contains any whitespace. Such URLs pass isValidHttpUrlWithDomain today
// because `new URL()` silently percent-encodes a space in the path/query
// (e.g. "https://example.com/a?a =1" -> ".../a?a%20=1"), so we flag whitespace separately.
export const urlHasSpace = (url?: string): boolean => !!url && WHITESPACE.test(url)

// Context around the FIRST whitespace char: up to 5 chars on each side, for a "m/a?a =1"
// style hint that helps locate the space in a long URL. index = -1 when there is no space.
export const getSpaceContext = (
  url: string
): { index: number; left: string; right: string } => {
  const index = url.search(WHITESPACE)
  if (index === -1) return { index: -1, left: '', right: '' }
  return {
    index,
    left: url.slice(Math.max(0, index - 5), index),
    right: url.slice(index + 1, index + 6),
  }
}

// Removes every whitespace character from the URL (used by the "Fix" button).
export const stripUrlSpaces = (url: string): string => url.replace(/\s+/g, '')

// Verbal locator for the first whitespace: quotes up to 5 chars on each side (with … where the
// URL continues) so a message reads e.g. `between "…m/a?a" and "=1"`. Empty when there's no space.
export const describeSpaceLocation = (url: string): string => {
  const { index, left, right } = getSpaceContext(url)
  if (index === -1) return ''
  const lead = index - 5 > 0 ? '…' : ''
  const trail = index + 6 < url.length ? '…' : ''
  const before = left ? `"${lead}${left}"` : 'the start'
  const after = right ? `"${right}${trail}"` : 'the end'
  return `between ${before} and ${after}`
}

// react-hook-form validate rule: invalid (blocks the field) when the URL has a space, with a
// message that says where it is. Pair with a UrlSpaceFixButton to offer one-click removal.
export const noSpaceRule = (url?: string): true | string =>
  !urlHasSpace(url) || `URL contains a space ${describeSpaceLocation(url || '')}`

const URL_FORMAT_MESSAGE =
  'Enter a valid URL with http/https and a domain (e.g. https://example.com)'

// react-hook-form validate rule: a valid http(s) URL with a domain (required field).
export const httpUrlRule = (url?: string): true | string =>
  isValidHttpUrlWithDomain(url) || URL_FORMAT_MESSAGE

// Same, but treats an empty value as valid (optional field).
export const optionalHttpUrlRule = (url?: string): true | string =>
  !url || isValidHttpUrlWithDomain(url) || URL_FORMAT_MESSAGE

// The API caps targetValue at 1,000,000,000 (CON-538, MAX_TARGET_VALUE). The ceiling sits below
// every Epom limit; above it Epom answers the campaign sync with a 400, which deactivates the
// campaign. Mirror the cap here so the form fails fast instead of failing on Save.
export const TARGET_VALUE_MAX = 1_000_000_000

// Danish grouping ("1.000.000.000"), matching the app's audience and the comma decimal separator
// on the input itself (InputNumber.tsx). The locale is explicit rather than the runtime default
// because the message is asserted in tests.
const TARGET_VALUE_RANGE_MESSAGE =
  `Must be greater than 0 and no more than ${TARGET_VALUE_MAX.toLocaleString('da-DK')}`

// react-hook-form validate rule for the campaign goal value. The lower bound is EXCLUSIVE: the API
// rejects `<= 0` for every real target type, so accepting 0 here would only defer the failure to
// Save. Absent values are valid — presence is a separate rule, and the field is legitimately empty
// for non-BUDGET target types.
export const targetValueRangeRule = (value?: number | string | null): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  const num = Number(value)

  return (Number.isFinite(num) && num > 0 && num <= TARGET_VALUE_MAX) || TARGET_VALUE_RANGE_MESSAGE
}

// Mirrors COUNT_BASED_TARGET_TYPES in the API's IsValidTargetValue: these goals are counts of
// whole things, so the API rejects decimals. BUDGET and CPM may be fractional.
const COUNT_BASED_TARGET_TYPES = [
  CampaignTargetType.Impressions,
  CampaignTargetType.Reads,
  CampaignTargetType.Clickouts,
]

const WHOLE_NUMBER_MESSAGE = 'Must be a whole number'

export const targetValueWholeNumberRule = (
  value?: number | string | null,
  targetType?: CampaignTargetType | null,
): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  if (!targetType || !COUNT_BASED_TARGET_TYPES.includes(targetType)) {
    return true
  }

  const num = Number(value)

  // Non-finite input is targetValueRangeRule's business — don't report it twice.
  return !Number.isFinite(num) || Number.isInteger(num) || WHOLE_NUMBER_MESSAGE
}

export const DAILY_BUDGET_LIMIT_MAX = 1_000_000_000

const DAILY_BUDGET_LIMIT_RANGE_MESSAGE =
  `Must be between 0 and ${DAILY_BUDGET_LIMIT_MAX.toLocaleString('da-DK')}`

export const dailyBudgetLimitRangeRule = (value?: number | string | null): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  const num = Number(value)

  return (
    (Number.isFinite(num) && num >= 0 && num <= DAILY_BUDGET_LIMIT_MAX) ||
    DAILY_BUDGET_LIMIT_RANGE_MESSAGE
  )
}
